/**
 * Contracts. The thirty authored jobs the Sponsors of Record put on the board.
 *
 * **THE BOARD IS NOT OPEN.** There is no contracts route in `server/src`, no contract table in
 * any migration, and `loadContracts` has no caller: `tools/content/validate.mjs` carries the debt
 * by name in `READER_OWED`. What this screen renders is the thirty authored contracts and the
 * ceremony one of them will run, which are the two things that are true today.
 *
 * What it used to render, and every one of these is a deletion rather than a correction:
 *
 *   - **Fifteen invented sponsors.** CONTENT-BIBLE 3.2 authors exactly twelve Sponsors of Record,
 *     `content/sponsors.json` implements them, and `content/contracts.json` names one per
 *     contract. This file typed fifteen others, put Vincenzo Aiello on Packy Coughlin's portrait
 *     three times when the bible excludes him from the board by name, and listed the Precinct
 *     Captain as an issuer when he is the Heat sink and not a patron.
 *   - **A "Respected standing" gate**, a reputation ladder that exists in no document, no
 *     formula and no column.
 *   - Dollar rewards, expiry countdowns and slot fillers for a system with no storage.
 *
 * The thirty come from `content/contracts.json` by direct import, the same way
 * `game/src/game/eras.ts` reads `content/eras.json`. Every sponsor name, scene, seal and brief on
 * this screen is the one the writer wrote, and the content gate proves exactly-once coverage over
 * all thirty `contracts.scenes.*` artworks on every build.
 */

import { useState } from 'react'
import { A } from '../art'
import type { KnownAssetId } from '../art'
import contracts from '../../../content/contracts.json'
import { Icon } from '../ui/Icon'
import { Art, Chip, Col, Panel, PanelHead, Rail, Row, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'

interface ContractRow {
  id: string
  n: number
  title: string
  type: string
  typeIconId: string
  sceneId: string
  sponsorName: string
  sponsorArtId: string
  sealId: string
  risk: string
  nerve: number
  brief: string
}

const ROWS: ContractRow[] = (contracts as ContractRow[]).slice().sort((a, b) => a.n - b.n)

const TYPE_TINT: Record<string, string> = {
  elimination: 'var(--arrest)',
  disruption: 'var(--heat)',
  sabotage: 'var(--gold)',
  transport: 'var(--police)',
  extortion: 'var(--dirty)',
  protection: 'var(--clean)',
}

const RISK_TINT: Record<string, string> = {
  low: 'var(--clean)',
  medium: 'var(--gold)',
  high: 'var(--heat)',
  extreme: 'var(--arrest)',
}

const TYPES = [...new Set(ROWS.map((r) => r.type))].sort()

type TabId = 'board' | 'sitdown'

function BoardTab({
  type,
  onPick,
  picked,
}: {
  type: string | null
  onPick: (id: string) => void
  picked: string | null
}) {
  const rows = type === null ? ROWS : ROWS.filter((r) => r.type === type)
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Board"
        icon="contracts"
        sub="Thirty jobs, written and not yet open"
        right={
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
            {rows.length} shown
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '8px 10px 10px' }}>
        <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          None of these can be taken yet. A Contract needs a third player in the room, which is
          the whole reason it exists, and the routes that seat one are not built.
        </p>
        <Col gap={6}>
          {rows.map((r) => {
            const on = picked === r.id
            return (
              <div key={r.id} onClick={() => onPick(r.id)} style={{ cursor: 'pointer' }}>
                <Row
                  gap={9}
                  style={{
                    padding: '7px 8px',
                    background: on ? 'rgba(214,168,75,.08)' : 'rgba(255,255,255,.02)',
                    border: `1px solid ${on ? 'var(--line-brass)' : 'var(--line-soft)'}`,
                    alignItems: 'flex-start',
                  }}
                >
                  <Art
                    src={A(r.sceneId as KnownAssetId)}
                    alt={r.title}
                    scrim="none"
                    style={{ width: 78, height: 50, flex: 'none', border: '1px solid var(--line)' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Row gap={7} style={{ justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{r.title}</span>
                      <Row gap={5}>
                        <Chip c={TYPE_TINT[r.type] ?? 'var(--ink-dim)'}>{r.type}</Chip>
                        <Chip c={RISK_TINT[r.risk] ?? 'var(--ink-dim)'}>{r.risk}</Chip>
                      </Row>
                    </Row>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.45 }}>
                      {r.brief}
                    </p>
                    <Row gap={9} style={{ marginTop: 5 }}>
                      {/*
                        * The sponsor's NAME and the sponsor's PORTRAIT come off the same row.
                        * `extract.mjs` used to assign the portrait positionally, so all thirty
                        * belonged to another sponsor in a clean twelve-permutation and 25 of 30
                        * seals were wrong. Fixed at the extractor; held by the sponsors pin.
                        */}
                      <img
                        src={A(r.sponsorArtId as KnownAssetId)}
                        alt={r.sponsorName}
                        style={{ width: 20, height: 20, objectFit: 'cover', border: '1px solid var(--line)' }}
                      />
                      <span className="t-label" style={{ fontSize: sz(9) }}>{r.sponsorName}</span>
                      <div style={{ flex: 1 }} />
                      <Icon n="nerve" s={11} c="var(--nerve)" />
                      <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--nerve)' }}>
                        {r.nerve} Nerve
                      </span>
                    </Row>
                  </div>
                </Row>
              </div>
            )
          })}
        </Col>
      </div>
    </Panel>
  )
}

/**
 * The Sit-Down: the Contract half of the vouch ceremony, published before it is built.
 *
 * `docs/PHASE-6B.md` section 7.2 rules it in full. The single most important sentence in that
 * design is the one about the roll, and it belongs on the screen rather than in a document,
 * because a sponsor deciding whether to stake 250,000 clean on somebody deserves to know it
 * cannot be lost to a die.
 */
function SitDownTab() {
  const seats: [string, string, string][] = [
    ['The Chair', '4 Nerve', 'The sponsor. Their stake is already held in escrow when they sit down.'],
    [
      'The Name',
      '2 Nerve',
      'The sponsee. This is the one Contract completed as a helper that the vouch requires.',
    ],
    [
      'The Witness',
      '1 Nerve',
      'A third player who ACCEPTS rather than being named, and who must hold at least one accepted vouch of their own this Era. You cannot speak for the room until the room has spoken for you.',
    ],
  ]
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead title="The Sit-Down" icon="rank" sub="How a vouch is actually made" />
      <div className="scroll-y" style={{ flex: 1, padding: '10px 12px 12px' }}>
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          Accepting a vouch does not complete it. It convenes a Sit-Down with three named seats,
          seven Nerve between them, which is exactly what one high-risk Contract costs and is split
          so no single seat pays as much as a low-risk one.
        </p>
        <div style={{ margin: '12px 0 0' }}>
          {seats.map(([name, cost, note]) => (
            <div key={name} style={{ marginBottom: 11 }}>
              <Row gap={8}>
                <span className="t-head" style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: '.1em' }}>
                  {name}
                </span>
                <Chip c="var(--nerve)">{cost}</Chip>
              </Row>
              <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>{note}</p>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 6,
            padding: '9px 11px',
            border: '1px solid var(--line-brass)',
            background: 'rgba(214,168,75,.07)',
          }}
        >
          <div className="t-head" style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: '.1em' }}>
            It cannot fail on a roll. The server rolls nothing.
          </div>
          <p style={{ margin: '5px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
            A sponsor's stake is never lost to variance, because variance on a social contract
            destroys the social contract. If all three do not commit inside 48 hours it lapses, the
            stake returns in full with no premium and no forfeit, the slot is released, and nothing
            is written to anybody's record. A lapse is not a failure and is not recorded as one.
          </p>
        </div>
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Contracts() {
  const [tab, setTab] = useState<TabId>('board')
  const [type, setType] = useState<string | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const detail = ROWS.find((r) => r.id === picked) ?? null
  const { stacked } = useViewport()

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 0', flexWrap: 'wrap' }}>
            <Icon n="contracts" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none', paddingBottom: 7 }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>Contracts</div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>Work that needs a room</div>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Tabs
                items={[
                  { id: 'board' as const, label: 'The Board' },
                  { id: 'sitdown' as const, label: 'The Sit-Down' },
                ]}
                value={tab}
                onChange={setTab}
              />
            </div>
            <div style={{ flex: 1 }} />
            <Row gap={6} style={{ flex: 'none', paddingBottom: 7 }}>
              <Chip c="var(--ink-dim)">Not open yet</Chip>
            </Row>
          </Row>
        </Panel>

        {tab === 'board' && <BoardTab type={type} onPick={setPicked} picked={picked} />}
        {tab === 'sitdown' && <SitDownTab />}
      </Col>

      <Rail width={300}>
        <Panel style={{ flex: 'none' }}>
          <PanelHead title="Type" icon="target" />
          <div style={{ padding: '6px 10px 10px' }}>
            <div onClick={() => setType(null)} style={{ cursor: 'pointer' }}>
              <Row gap={7} style={{ padding: '5px 4px', borderBottom: '1px solid var(--line-soft)' }}>
                <span style={{ flex: 1, fontSize: 11.5, color: type === null ? 'var(--gold)' : 'var(--ink)' }}>
                  Every type
                </span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>{ROWS.length}</span>
              </Row>
            </div>
            {TYPES.map((t) => (
              <div key={t} onClick={() => setType(t)} style={{ cursor: 'pointer' }}>
                <Row gap={7} style={{ padding: '5px 4px', borderBottom: '1px solid var(--line-soft)' }}>
                  <span style={{ flex: 1, fontSize: 11.5, color: type === t ? 'var(--gold)' : 'var(--ink)' }}>{t}</span>
                  <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>
                    {ROWS.filter((r) => r.type === t).length}
                  </span>
                </Row>
              </div>
            ))}
          </div>
        </Panel>

        {detail && (
          <Panel style={{ flex: 'none', overflow: 'hidden' }}>
            <PanelHead title={detail.title} icon="contracts" sub={`Contract ${detail.n} of ${ROWS.length}`} />
            <Art
              src={A(detail.sceneId as KnownAssetId)}
              alt={detail.title}
              style={{ height: 148, flex: 'none' }}
              scrim="soft"
            />
            <div style={{ padding: '10px 12px 12px' }}>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>{detail.brief}</p>
              <Row gap={9} style={{ marginTop: 10, alignItems: 'center' }}>
                <img
                  src={A(detail.sealId as KnownAssetId)}
                  alt={`The seal of ${detail.sponsorName}`}
                  style={{ width: 34, height: 34, objectFit: 'contain' }}
                />
                <div style={{ minWidth: 0 }}>
                  <div className="t-label" style={{ fontSize: sz(8.5) }}>Sponsor of Record</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{detail.sponsorName}</div>
                </div>
              </Row>
            </div>
          </Panel>
        )}
      </Rail>
    </Stack>
  )
}
